import React, { useState, useMemo, useEffect } from "react";
import BreadCrumb from "../Common/BreadCrumb";
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
} from "react-icons/fi";
import CareerAddEditModal from "../../Components/adminComponents/DashboardCareerComponents/CareerAddEditModal";

const DashboardCareers = () => {
  const initialJobs = [
    {
      id: 1,
      title: "Frontend Developer",
      location: "Bengaluru, India",
      type: "Full-time",
      posted: "2025-11-20",
      salary: "₹35,000 - ₹55,000",
      description: "React + Tailwind role building customer-facing apps.",
    },
    {
      id: 2,
      title: "Backend Engineer",
      location: "Hyderabad, India",
      type: "Full-time",
      posted: "2025-11-18",
      salary: "₹45,000 - ₹70,000",
      description: "Node.js/Express + MongoDB services.",
    },
    {
      id: 3,
      title: "UI/UX Designer",
      location: "Remote",
      type: "Contract",
      posted: "2025-11-10",
      salary: "₹25,000 - ₹40,000",
      description: "Design modern web experiences and prototypes.",
    },
    {
      id: 4,
      title: "Full-stack Intern",
      location: "Kolkata, India",
      type: "Internship",
      posted: "2025-10-28",
      salary: "Stipend",
      description: "Learn MERN stack building real features.",
    },
    {
      id: 5,
      title: "DevOps Engineer",
      location: "Mumbai, India",
      type: "Full-time",
      posted: "2025-11-01",
      salary: "₹60,000 - ₹95,000",
      description: "Kubernetes, CI/CD, AWS experience preferred.",
    },
    {
      id: 6,
      title: "Product Manager",
      location: "Pune, India",
      type: "Full-time",
      posted: "2025-09-14",
      salary: "₹80,000 - ₹1,20,000",
      description: "Lead product discovery and delivery.",
    },
  ];

  const [jobs, setJobs] = useState(initialJobs);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobToDelete, setJobToDelete] = useState(null);
  const postPerPage = 6;

  const filtered = useMemo(() => {
    if (!query) return jobs;
    const q = query.toLowerCase();
    return jobs.filter(
      (j) =>
        j.title.toLowerCase().includes(q) ||
        j.location.toLowerCase().includes(q)
    );
  }, [jobs, query]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / postPerPage));
  const paginated = useMemo(
    () => filtered.slice((page - 1) * postPerPage, page * postPerPage),
    [filtered, page]
  );

  function openAddModal() {
    setEditingJob(null);
    setIsModalOpen(true);
  }

  function openEditModal(job) {
    setEditingJob(job);
    setIsModalOpen(true);
  }

  // new onSave handler receives the job form from AddEditModal
  function handleSaveFromModal(form) {
    if (editingJob) {
      setJobs((prev) =>
        prev.map((j) => (j.id === editingJob.id ? { ...j, ...form } : j))
      );
    } else {
      const id = Math.max(0, ...jobs.map((j) => j.id)) + 1;
      setJobs((prev) => [{ id, ...form }, ...prev]);
      setPage(1);
    }

    setIsModalOpen(false);
    setEditingJob(null);
  }

  function confirmDelete(job) {
    setJobToDelete(job);
    setIsDeleteOpen(true);
  }

  function handleDelete() {
    if (!jobToDelete) return;
    setJobs((prev) => prev.filter((j) => j.id !== jobToDelete.id));
    setIsDeleteOpen(false);
    setJobToDelete(null);
  }

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [totalPages, page]);

  return (
    <div className="p-5">
      <BreadCrumb title="Dashboard Careers" trail={["Dashboard", "Careers"]} />

      <div className="flex items-center justify-between gap-4 mb-6">
        <p className="text-sm text-slate-500">
          Manage job postings — add, edit, delete and review applicants.
        </p>

        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-400">
              <FiSearch />
            </div>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search jobs or location"
              className="pl-10 pr-4 py-2 rounded-lg border border-slate-200 shadow-sm w-80 focus:outline-none focus:ring-2 focus:ring-[#5989C8] focus:border-transparent"
            />
          </div>

          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#5989C8] to-[#064585] text-white px-4 py-2 rounded-xl shadow-lg transform hover:-translate-y-0.5 transition"
          >
            <FiPlus /> Add Job
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {paginated.map((job) => (
          <div
            key={job.id}
            className="bg-white rounded-2xl p-5 shadow-md border border-transparent hover:shadow-xl hover:-translate-y-1 transition transform"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-medium text-slate-900">
                  {job.title}
                </h3>
                <p className="text-sm text-slate-500">{job.location}</p>

                <div className="mt-2 flex flex-col gap-4 text-xs">
                  <p>
                    <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 border">
                      {job.type}
                    </span>
                  </p>
                  <p>
                    <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 border">
                      {job.salary}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <div className="text-xs text-slate-400">
                  Posted {job.posted}
                </div>
                <div className="flex flex-col items-center gap-2 bg-slate-50 p-1 rounded-lg">
                  <button
                    onClick={() => openEditModal(job)}
                    title="Edit"
                    className="p-2 rounded hover:bg-slate-100"
                  >
                    <FiEdit />
                  </button>
                  <button
                    onClick={() => confirmDelete(job)}
                    title="Delete"
                    className="p-2 rounded hover:bg-slate-100 text-red-600"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>

            <p className="mt-4 text-sm text-slate-700 line-clamp-3">
              {job.description.slice(0, 40)}
              {job.description.length > 40 && "..."}
            </p>
          </div>
        ))}

        {paginated.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-500">
            No jobs found. Try adjusting your search or add a new job.
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-sm text-slate-600">
          <div>
            Showing{" "}
            <strong className="text-slate-900">
              {Math.min((page - 1) * postPerPage + 1, total)}
            </strong>{" "}
            -{" "}
            <strong className="text-slate-900">
              {Math.min(page * postPerPage, total)}
            </strong>{" "}
            of <strong className="text-slate-900">{total}</strong>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`p-2 rounded ${
              page === 1
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-slate-100"
            }`}
          >
            <FiChevronLeft />
          </button>

          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`h-8 w-8 rounded-full ${
                  page === i + 1
                    ? "bg-gradient-to-r from-[#5989C8] to-[#064585] text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={`p-2 rounded ${
              page === totalPages
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-slate-100"
            }`}
          >
            <FiChevronRight />
          </button>
        </div>
      </div>

      {/* Add / Edit modal component */}
      <CareerAddEditModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingJob(null);
        }}
        onSave={handleSaveFromModal}
        initialData={editingJob}
      />

      {/* Delete confirm */}
      {isDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-lg ring-1 ring-slate-100">
            <h3 className="text-lg font-semibold">Delete Job</h3>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete{" "}
              <strong>{jobToDelete?.title}</strong> ? This action cannot be
              undone.
            </p>

            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setIsDeleteOpen(false)}
                className="px-4 py-2 rounded-lg border"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-600 text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardCareers;
